import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {PlayerService} from '@app/components/player/player.service';
import {BrowserStorageService, DeviceService, EventTrackerService, HttpService, LogService} from '@app/core';
import {iBook, ProductTypeEnum} from '@app/interfaces/iBook';
import {NotificationService} from '@app/shared/notification/notification.service';
import {environment} from '@env/environment';
import {of, Subscription} from 'rxjs';
import {
  infoPlayer,
  IPurchase,
  PlayButtonAction,
  PlayButtonMode,
  purchaseButtonActionType
} from '../../pdp-model';
import {PDPService} from '../pdp.service';
import {IndicatorService} from "@app/shared";
import {Router} from "@angular/router";
import {AppParametersService} from "@app/core/token/app-parameters.service";
import {ClientTypeEnum} from "@app/helper/ClientTypeEnum";

export type animationPlayBtn = 'Play' | 'Waiting';

@Component({
  selector: 'navaar-pdp-button',
  templateUrl: './pdp-button.component.html',
  styleUrls: ['./pdp-button.component.scss']
})
export class PdpButtonComponent implements OnInit, OnDestroy {
  @Output('show-download-dialog') public showDownloadDialog: EventEmitter<void> = new EventEmitter<void>();
  @Input() public isLogin: boolean = false;
  @Input() public dataBook: iBook | null;

  @Input()
  public set purchase(value: IPurchase | null) {
    if (value) {
      this._purchase = value;
      this.isLoading = false;
    }
  }

  public get purchase(): IPurchase | null {
    return this._purchase;
  }

  @Output('open-dialog-download') public openDialogPopup: EventEmitter<void> = new EventEmitter<void>();
  @Output('open-dialog-market') public openDialogMarket: EventEmitter<purchaseButtonActionType> = new EventEmitter<purchaseButtonActionType>();

  public subscription: Subscription = new Subscription();
  private _purchase: IPurchase;
  public isPlaying: boolean = false;
  public isLoading: boolean = true;
  public showLoaderPlay: boolean = false;
  public stylePlayerIcon: boolean = false;
  public productType: typeof ProductTypeEnum = ProductTypeEnum;
  public playButtonMode: typeof PlayButtonMode = PlayButtonMode;
  public clientType: typeof ClientTypeEnum = ClientTypeEnum;

  constructor(
    private readonly storage: BrowserStorageService,
    private readonly log: LogService,
    private readonly pdpServices: PDPService,
    public readonly device: DeviceService,
    public readonly appParameterService: AppParametersService,
    private readonly indicator: IndicatorService,
    private readonly eventTracker: EventTrackerService,
    private readonly notification: NotificationService,
    public readonly http: HttpService,
    public readonly playerService: PlayerService,
    public readonly pdpService: PDPService,
    private readonly router: Router,
    private readonly chkd: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    if (this.isLogin === false && !this.purchase)
      this.isLoading = false;
    this.subscription.add(this.pdpService.statusPlay$.subscribe(c => {
      this.chkd.markForCheck();
    }));
    this.subscription.add(this.playerService.statusPlayBtnSource.subscribe(c => {
      this.chkd.markForCheck();
      if (c.isPlaying) {
        this.isPlaying = true;
      } else if (c.isLoading) {
        this.showLoaderPlay = true;
        this.stylePlayerIcon = true;
        this.isPlaying = false;
      }
      if (c.isEnd) {
        this.isPlaying = false;
        this.showLoaderPlay = false;
        this.stylePlayerIcon = true;
      }
    }));
  }

  public onPurchase() {
    let productId = this.dataBook.products[0]['productId'];
    this.log.logger('Sending purchase with ' + productId);
    if (this.device.isMarkets()) {
      this.openDialogMarket.emit(this.purchase.purchaseButtonState.purchaseButtonAction);
    } else {
      this.device.purchaseBook(productId);
    }

    this.logItem('x_btn_get_item', {
      'x_subscription_status': this.purchase?.newSubscriptionButtonState?.subscriptionButtonState,
      'x_purchase_status': this.purchase?.pdpActionButtonState?.playButtonState.playButtonAction,
      'x_button_type': 'purchase_btn_pdp_page',
      'x_button_productId': productId
    });
    this.indicator.show(true);
  }

  public async onSubscription() {
    let x_button_type = 'purchase-in-pdp';
    const info: infoPlayer = {
      audioBookId: this.dataBook.audioBookId,
      title: this.dataBook.title,
      image: `${environment.imageUrl}/content/books/${this.dataBook.audioBookId}/pic.jpg?t=${this.dataBook.recordVersion}`,
      isOwner: true,
      identifier: this.dataBook.identifier,
      genreIdentifier: this.dataBook.genres[0]?.parentIdentifier,
      genreTitle: this.dataBook.genres[0]?.parentTitle
    };
    switch (this.dataBook.contentType) {
      case ProductTypeEnum.AudioBook:
      case ProductTypeEnum.Capsule:
        info.narrators = this.dataBook['narrators'][0] && (this.dataBook['narrators'][0].firstName + ' ' + this.dataBook['narrators'][0].lastName);
        info.author = (this.dataBook['authors'][0]) && (this.dataBook['authors'][0].firstName + ' ' + this.dataBook['authors'][0].lastName);
        break;
      case ProductTypeEnum.AudioSeries:
        info.author = (this.dataBook['authors'][0]) && (this.dataBook['authors'][0].firstName + ' ' + this.dataBook['authors'][0].lastName);
        break;
      case ProductTypeEnum.Podcast:
      default:
        info.author = "";
    }
    switch (this.purchase?.pdpActionButtonState?.playButtonState?.playButtonAction) {
      case PlayButtonAction.NavigateToSubscriptionPage:
        x_button_type = 'x_btn_download_popup_SubscriptionPlans';
        this.logItem('x_btn_get_item', {
          'x_subscription_status': this.purchase?.newSubscriptionButtonState?.subscriptionButtonState,
          'x_purchase_status': this.purchase?.pdpActionButtonState?.playButtonState,
          'x_button_type': x_button_type
        });
        this.indicator.show(true);
        this.router.navigate(['./subscription', 'plans']).then(_ => this.indicator.hide());
        break;
      case PlayButtonAction.AddFreeAndPlay:
        this.indicator.showHude();
        x_button_type = 'x_btn_download_no_payment'
        this.logItem('x_btn_get_item', {
          'x_subscription_status': (this.purchase.subscriptionButtonState || {}).subscriptionButtonState,
          'x_purchase_status': this.purchase.purchaseButtonState.purchaseButtonAction,
          'x_button_type': x_button_type
        });
        this.http.post(`/api/orders/app-nopayment?productId=${this.dataBook.products[0]['productId']}`, {})
          .subscribe((response) => {
            this.indicator.hideHude();
            this.pdpServices.play(info);
          }, (error) => {
            this.indicator.hideHude();
            if (error && error.hasOwnProperty('error') && error.error.hasOwnProperty('message')) {
              this.notification.setNotification({text: error.error.message, type: 'error', timeout: 5000});
            }
          });
        break;
      case PlayButtonAction.PlayPurchased:
        this.indicator.showHude(true);
        this.pdpServices.play(info);
        break;
      case PlayButtonAction.PlayNewSubscription:
        this.indicator.showHude();
        this.purchaseNewSubscription(info);
        break;
      case PlayButtonAction.OpenCartDialog:
        this.openDialogPopup.emit();
        break;
    }

    this.logItem('x_btn_get_item', {
      'x_subscription_status': (this.purchase.subscriptionButtonState || {}).subscriptionButtonState,
      'x_purchase_status': this.purchase.purchaseButtonState.purchaseButtonAction,
      'x_button_type': x_button_type
    });
  }

  public goToSubscription() {
    this.indicator.show(true);
    this.router.navigate(['./subscription', 'plans']).then(_ => this.indicator.hide());
  }

  public async playSample() {
    const info: infoPlayer = {
      audioBookId: this.dataBook.audioBookId,
      title: this.dataBook.title,
      image: `${environment.imageUrl}/content/books/${this.dataBook.audioBookId}/pic.jpg?t=${this.dataBook.recordVersion}`,
      isOwner: false,
      identifier: this.dataBook.identifier,
      genreIdentifier: this.dataBook.genres[0]?.parentIdentifier,
      genreTitle: this.dataBook.genres[0]?.parentTitle
    };
    switch (this.dataBook.contentType) {
      case this.productType.AudioBook:
      case this.productType.Capsule:
        info.narrators = this.dataBook['narrators'][0] && (this.dataBook['narrators'][0].firstName + ' ' + this.dataBook['narrators'][0].lastName);
        info.author = (this.dataBook['authors'][0]) && (this.dataBook['authors'][0].firstName + ' ' + this.dataBook['authors'][0].lastName);
        break;
      case this.productType.AudioSeries:
        info.author = (this.dataBook['authors'][0]) && (this.dataBook['authors'][0].firstName + ' ' + this.dataBook['authors'][0].lastName);
        break;
      case this.productType.Podcast:
      default:
        info.author = "";
    }
    this.pdpService.play(info);
    this.logItem('x_btn_listen_audiobook', {
      'x_subscription_status': this.isLogin ? this.purchase?.pdpActionButtonState?.playButtonState?.playButtonAction : '',
      'x_purchase_status': this.isLogin ? this.purchase?.purchaseButtonState?.purchaseButtonAction : '',
      'x_button_type': 'sample',
      'x_is_login': this.isLogin
    })
  }

  private purchaseNewSubscription(info: infoPlayer) {
    this.pdpServices.purchaseNewSubscriptionMode(this.dataBook.products[0]['productId']).subscribe((response) => {
      this.indicator.hideHude();
      this.pdpServices.play(info);
      this.logItem('x_btn_get_item', {
        'x_subscription_status': (this.purchase.subscriptionButtonState || {}).subscriptionButtonState,
        'x_purchase_status': this.purchase.purchaseButtonState.purchaseButtonAction,
        'x_button_type': 'subscription'
      });
    }, (error) => this.indicator.hideHude());
  }

  private logItem(name: string, obj: object) {
    let _obj = {
      ...{
        item_id: 'this.dataBook?.identifier',
        'audiobook-title': 'this.dataBook?.title'
      }, ...obj
    };
    this.eventTracker.logEvent(name, _obj);
  }

  public purchaseLogoutMode() {
    this.device.goTologinPageNative(this.router.url);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
